// Applies reviewed guidance while preserving the exact previous edition fields.
const GUIDANCE_FIELDS = ['directionText', 'spokenDirectionText', 'spokenDirectionPending', 'directionCaptionOnly'];
const fail = message => { throw new Error(`Reviewed acting guidance: ${message}`); };

function reviewedRows(storyId, cues, review) {
  if (!review || review.storyId !== storyId || review.approvalStatus !== 'OWNER_ADOPTED_AFTER_INDEPENDENT_REVIEW'
      || !Array.isArray(cues) || !Array.isArray(review.rows) || !Number.isInteger(review.expectedCount)
      || review.expectedCount < 1 || review.rows.length !== review.expectedCount) fail('unaccepted edition');
  const indexes = new Map();
  for (const [index, cue] of cues.entries()) {
    if (typeof cue?.id !== 'string' || !cue.id || indexes.has(cue.id)) fail('invalid or repeated source cue');
    indexes.set(cue.id, index);
  }
  const rows = new Map(); let previousIndex = -1;
  for (const row of review.rows) {
    const index = indexes.get(row?.cueId);
    if (index === undefined || index <= previousIndex || rows.has(row.cueId)) fail('changed cue order or identity');
    previousIndex = index;
    rows.set(row.cueId, row);
  }
  return rows;
}

function validateRetainedFields(row, cue) {
  const before = row.beforeGuidanceFields;
  if (!before || typeof before !== 'object' || Array.isArray(before)
      || ![Object.prototype, null].includes(Object.getPrototypeOf(before))
      || Object.keys(before).some(key => !GUIDANCE_FIELDS.includes(key))
      || !Object.hasOwn(before, 'spokenDirectionPending') || before.spokenDirectionPending !== true
      || before.spokenDirectionText || (before.directionText || null) !== row.sourceDirectionText) fail('invalid retained cue preimage');
  if (cue && GUIDANCE_FIELDS.some(key => !Object.is(cue[key], before[key]))) fail('retained cue preimage differs');
}

function validateGuidanceText(row) {
  if (typeof row.fullDirectionText !== 'string' || !row.fullDirectionText.trim() || row.fullDirectionText.length > 4000
      || (row.sourceDirectionText !== null && row.fullDirectionText !== row.sourceDirectionText)) fail('changed existing full guidance');
  if (typeof row.spokenDirectionText !== 'string' || !row.spokenDirectionText.trim() || row.spokenDirectionText.length > 4000
      || row.spokenDirectionText.trim().split(/\s+/).length > 4) fail('short direction needs one to four bounded words');
}

const matchesIdentity = (cue, row) => cue.type === 'dialogue' && cue.characterId === row.characterId
  && cue.sceneId === row.sceneId && cue.dialogueText === row.sourceDialogueText;
const clearFlag = value => value === undefined || value === false;

// Every adopted row binds retained values to the actual source cue. Absent
// fields and own undefined values share the same JSON/normalized signature.
// The review producer must populate beforeGuidanceFields from the actual cue.
export function applyReviewedActingGuidance(storyId, cues, review) {
  if (!review) return cues;
  const rows = reviewedRows(storyId, cues, review);
  for (const cue of cues) {
    const row = rows.get(cue.id);
    if (!row) continue;
    if (!matchesIdentity(cue, row) || (cue.directionText || null) !== row.sourceDirectionText
        || cue.spokenDirectionPending !== true || cue.spokenDirectionText) fail('source preimage differs');
    validateRetainedFields(row, cue);
    validateGuidanceText(row);
  }
  return cues.map(cue => {
    const row = rows.get(cue.id);
    return row ? {...cue, directionText: row.fullDirectionText, spokenDirectionText: row.spokenDirectionText,
      spokenDirectionPending: undefined, directionCaptionOnly: undefined} : cue;
  });
}

// Builds only a candidate for the existing exact previous-edition signature
// lookup. The caller must neither save this candidate nor bypass that lookup.
export function restorePreviousActingGuidance(scenario, review) {
  const rows = reviewedRows(scenario.id, scenario.cues, review);
  if (!Number.isInteger(review.beforeCatalogRevision) || review.beforeCatalogRevision < 1) fail('invalid previous edition revision');
  const cues = scenario.cues.map(cue => {
    const row = rows.get(cue.id);
    if (!row) return cue;
    validateRetainedFields(row);
    validateGuidanceText(row);
    if (!matchesIdentity(cue, row) || cue.directionText !== row.fullDirectionText
        || cue.spokenDirectionText !== row.spokenDirectionText
        || !clearFlag(cue.spokenDirectionPending) || !clearFlag(cue.directionCaptionOnly)) fail('changed reviewed acting guidance');
    const retained = {...cue};
    for (const key of GUIDANCE_FIELDS) delete retained[key];
    Object.assign(retained, row.beforeGuidanceFields);
    return retained;
  });
  return {...scenario, cues, catalogRevision: review.beforeCatalogRevision};
}
